/**
 * AppsFlyer writes (+ the imperative validate-event probe, a GET driven by a
 * user step). Module-private async fns returned from a factory; each unwraps
 * `{ data: T }` and throws ApiError on failure.
 */
import { apiClient } from '@discovr/common';
import type {
  AppsflyerConfigInput,
  AppsflyerConnectResult,
  AppsflyerEventValidation,
  CreatorCampaignLink,
  DataResponse,
  MintResult,
  RefreshResult,
} from '@discovr/types';
import { brandBase, campaignBase, getData } from './shared';

async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.post<DataResponse<T>>(url, body);
  return res.data.data;
}

const connect = (brandId: string, token: string): Promise<AppsflyerConnectResult> =>
  post<AppsflyerConnectResult>(`${brandBase(brandId)}/connect`, { token });

const disconnect = (
  brandId: string,
  feedback?: { reason?: string; notes?: string },
): Promise<{ disconnected: boolean }> =>
  post<{ disconnected: boolean }>(`${brandBase(brandId)}/disconnect`, feedback);

const validateEvent = (
  brandId: string,
  appId: string,
  eventName?: string,
): Promise<AppsflyerEventValidation> => {
  const qs = new URLSearchParams({ app_id: appId });
  if (eventName) qs.set('event_name', eventName);
  return getData<AppsflyerEventValidation>(`${brandBase(brandId)}/events?${qs.toString()}`);
};

const validateTemplate = (brandId: string, templateId: string): Promise<{ valid: boolean }> =>
  post<{ valid: boolean }>(`${brandBase(brandId)}/validate-template`, { template_id: templateId });

const saveConfig = (
  campaignId: string,
  input: AppsflyerConfigInput,
): Promise<{ saved: boolean; campaign_id: string }> =>
  post<{ saved: boolean; campaign_id: string }>(`${campaignBase(campaignId)}/config`, input);

const generateLinks = (campaignId: string): Promise<MintResult> =>
  post<MintResult>(`${campaignBase(campaignId)}/links/generate`);

const retryLink = (campaignId: string, creatorId: string): Promise<CreatorCampaignLink> =>
  post<CreatorCampaignLink>(
    `${campaignBase(campaignId)}/creators/${encodeURIComponent(creatorId)}/links/retry`,
  );

const refresh = (campaignId: string): Promise<RefreshResult> =>
  post<RefreshResult>(`${campaignBase(campaignId)}/refresh`);

export function createAppsflyerMutationsApi() {
  return {
    connect,
    disconnect,
    validateEvent,
    validateTemplate,
    saveConfig,
    generateLinks,
    retryLink,
    refresh,
  };
}

export type AppsflyerMutationsApi = ReturnType<typeof createAppsflyerMutationsApi>;
